import { useEffect, type RefObject } from "react"
import { prefersReducedMotion } from "../landing/motion"

/**
 * The site's motion on scroll (design/nira/boards/how-we-work-motion.html, handoff 5):
 *
 * - `[data-rv]` blocks play once as they come into view and never hide again. The kind names the
 *   motion in site.css: "up" (the children rise in order), "stagger" (a grid's items, one after
 *   another), "dissect" (the dissected answer: card, blocks, markers, connectors, notes), "three"
 *   (the three questions tick in).
 * - `[data-stage="hero"]` plays on load, from its visible frame.
 * - What is on the first screen is complete at rest: only blocks below it start hidden.
 * - A fast scroll past a block still reveals it, and after six seconds nothing stays hidden,
 *   whatever happens. Without IntersectionObserver, with reduced motion, and in print, everything is
 *   simply there: the hiding is switched on here, never by the stylesheet alone.
 */
export function useReveal(root: RefObject<HTMLElement>, key: unknown) {
  useEffect(() => {
    const el = root.current
    if (!el) return
    const targets = Array.from(el.querySelectorAll<HTMLElement>("[data-rv], [data-stage]"))
    if (!targets.length) return
    const show = (t: Element) => { t.classList.remove("pre"); t.classList.add("in") }
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      targets.forEach(show)
      return
    }
    el.classList.add("nst-motion")
    const vh = window.innerHeight
    for (const t of targets) {
      const hero = t.dataset.stage === "hero"
      if (hero || t.getBoundingClientRect().top > vh * 0.92) t.classList.add("pre")
      else show(t)
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue
          show(e.target)
          io.unobserve(e.target)
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    )
    targets.forEach((t) => { if (t.classList.contains("pre") && t.dataset.stage !== "hero") io.observe(t) })
    // The hero plays on load, from its visible frame.
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => targets.forEach((t) => { if (t.dataset.stage === "hero") show(t) }))
    })
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        const h = window.innerHeight
        targets.forEach((t) => { if (t.classList.contains("pre") && t.getBoundingClientRect().top < h) show(t) })
      })
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    const failsafe = window.setTimeout(() => targets.forEach(show), 6000)
    return () => {
      io.disconnect()
      cancelAnimationFrame(raf)
      window.removeEventListener("scroll", onScroll)
      clearTimeout(failsafe)
    }
  }, [root, key])
}
